import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { ProductGridSkeleton } from '../components/Skeleton';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as searchApi from '../api/endpoints/search';
import { useCart } from '../context/CartContext';
import { containerWide, pageSection, searchInputClass } from '../styles/ui';

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(q);
  const [state, setState] = useState({ status: 'idle', data: null, error: null });
  const { addItem, setQuantity, quantityFor } = useCart();

  // Arriving here from the navbar search (or the back button) changes `q`
  // without remounting, so mirror it into the field rather than leaving the
  // previous term visible above fresh results.
  useEffect(() => {
    setInputValue(q);
  }, [q]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next);
  };

  usePageMetadata({
    title: q ? `Search: ${q} | Fresh & Homely` : 'Search | Fresh & Homely',
    description: 'Search fresh meat, seafood, dairy and grocery products at Fresh & Homely.',
    canonicalPath: '/search',
  });

  const load = () => {
    if (!q) {
      setState({ status: 'idle', data: null, error: null });
      return;
    }
    setState({ status: 'loading', data: null, error: null });
    searchApi
      .search(q, 40)
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((err) => setState({ status: 'error', data: null, error: err.message }));
  };

  useEffect(load, [q]);

  const results = state.data?.products || state.data?.data || (Array.isArray(state.data) ? state.data : []);

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerWide}>
          <form onSubmit={handleSubmit} className="max-w-xl mx-auto mb-12 relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} aria-hidden="true" />
            <input
              type="search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search for products..."
              aria-label="Search products"
              className={searchInputClass}
            />
          </form>

          {state.status === 'idle' && (
            <EmptyState icon={SearchIcon} title="Search our catalog" description="Find fresh meat, seafood, dairy and groceries." />
          )}
          {state.status === 'loading' && <ProductGridSkeleton count={8} />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && results.length === 0 && (
            <EmptyState icon={SearchIcon} title="No results" description={`We couldn't find anything for "${q}".`} />
          )}
          {state.status === 'success' && results.length > 0 && (
            <>
              <p className="text-sm text-ink-secondary mb-6">
                {results.length} result{results.length === 1 ? '' : 's'} for &ldquo;{q}&rdquo;
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {results.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    quantity={quantityFor(product._id)}
                    onAddToCart={addItem}
                    onSetQuantity={setQuantity}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default SearchResultsPage;
